import MeshWriter, { Attr, AttrMap } from './mesh-writer';
import { PlaneMesh } from './plane-mesh';

/**
 * This is a bit different to the DiagridFaceMesh, which only provides a
 * repeatable subunit of the face grid. Here we provide the entire face grid
 * in one go.
 */

export class GridFaceMesh extends MeshWriter {
    width: number;
    height: number;
    thickness: number;
    countX: number;
    countY: number;

    constructor(width: number, height: number, thickness: number,
        countX: number, countY: number) {
        super();
        this.width = width;
        this.height = height;
        this.thickness = thickness;
        this.countX = countX;
        this.countY = countY;
    }

    get attrs() {
        // Count is how many vertexes we have... since we're working with
        // triangles, this is always a multiple of 3. Our horizontal quads run
        // the full span, uninterrupted. Our vertical quads are interrupted by
        // each of the horizontals, so they're multiplied by the number of
        // horizontal spans.
        const
            result = new Set<Attr>(), cx = this.countX, cy = this.countY, h = cy - 1, // the count of horizontal quads
            v = (cx - 1) * cy, // the count of vertical quads
            count = 6 * (h + v); // two tris and three verts (6) per quad


        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            w = this.width, h = this.height, t = this.thickness, t2 = t / 2, cx = this.countX, cy = this.countY, hinc = (w - t * (cx - 1)) / cx, // distance between horizals
            vinc = (h - t * (cy - 1)) / cy, // distance between verticals
            ut = t / w, // the thickness as a portion of the width
            vt = vinc / w, [_p, pos] = attrMap.byName('position'), [_u, uv] = attrMap.byName('uv');

        // Write the horizontal quads and the vertical quads above them
        let j = 0, k = 0;
        for (let i = 1; i < cy; i++) {
            const
                y0 = i * vinc + t * (i - 1), y1 = y0 + t, y2 = y1 + vinc, // the top of the vertical quads for this row
                u0 = i * ut, u1 = u0 + ut;
            j = PlaneMesh.write3(pos, j,
                0, y0, 0, // lower left
                w, y0, 0, // lower right
                w, y1, 0, // upper right
                0, y1, 0); // upper left
            k = PlaneMesh.write2(uv, k,
                u1, 0, // lower left
                u1, 1, // lower right
                u0, 1, // upper right
                u0, 0); // upper left


            // write the vertical quads
            for (let m = 1; m < cx; m++) {
                const
                    x0 = m * hinc + t * (m - 1), x1 = x0 + t, v0 = i * vt, v1 = v0 + vt;
                j = PlaneMesh.write3(pos, j,
                    x0, y1, 0, // lower left
                    x1, y1, 0, // lower right
                    x1, y2, 0, // upper right
                    x0, y2, 0); // upper left
                k = PlaneMesh.write2(uv, k,
                    0, v0, // lower left
                    ut, v0, // lower right
                    ut, v1, // upper right
                    0, v1); // upper left
            }
        }
        // Write the last row of vertical quads (right at the bottom)
        // write the vertical quads
        const y1 = 0, y2 = vinc;
        for (let m = 1; m < cx; m++) {
            const
                x0 = m * hinc + t * (m - 1), x1 = x0 + t, v0 = 0, v1 = vt;
            j = PlaneMesh.write3(pos, j,
                x0, y1, 0, // lower left
                x1, y1, 0, // lower right
                x1, y2, 0, // upper right
                x0, y2, 0); // upper left
            k = PlaneMesh.write2(uv, k,
                0, v0, // lower left
                ut, v0, // lower right
                ut, v1, // upper right
                0, v1); // upper left
        }
    }
}
/**
 * This matches the GridFaceMesh in that it produces all the boxes in one go.
 */

export class GridBoxMesh extends MeshWriter {
    width: number;
    height: number;
    depth: number;
    thickness: number;
    countX: number;
    countY: number;

    constructor(width: number, height: number, depth: number, thickness: number,
        countX: number, countY: number) {
        super();
        this.width = width;
        this.height = height;
        this.depth = depth;
        this.thickness = thickness;
        this.countX = countX;
        this.countY = countY;
    }

    get attrs() {
        // Count is how many vertexes we have... since we're working with
        // triangles, this is always a multiple of 3. Our horizontal quads run
        // the full span, uninterrupted. Our vertical quads are interrupted by
        // each of the horizontals, so they're multiplied by the number of
        // horizontal spans.
        const
            result = new Set<Attr>(), cx = this.countX, cy = this.countY, h = 2 * (cy - 1), // the count of horizontal quads
            v = cy * 2 * (cx - 1), // the count of vertical quads
            count = 6 * (h + v); // two tris and three verts (6) per quad


        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));
        result.add(new Attr('normal', 3, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            w = this.width, h = this.height, d = this.depth, t = this.thickness, t2 = t / 2, cx = this.countX, cy = this.countY, hinc = (w - t * (cx - 1)) / cx, // distance between horizals
            vinc = (h - t * (cy - 1)) / cy, // distance between verticals
            ut = t / w, // the thickness as a portion of the width
            vt = vinc / w, [_p, pos] = attrMap.byName('position'), [_u, uv] = attrMap.byName('uv'), [_n, normal] = attrMap.byName('normal');

        // Write the horizontal quads and the vertical quads above them
        let j = 0, k = 0, n = 0;
        for (let i = 1; i < cy; i++) {
            const
                y0 = i * vinc + t * (i - 1), y1 = y0 + t, y2 = y1 + vinc, // the top of the vertical quads for this row
                v0 = 0, v1 = d / w;
            // Lower horizontal quad
            j = PlaneMesh.write3(pos, j,
                0, y0, 0, // back, lower left
                w, y0, 0, // back, lower right
                w, y0, d, // front, lower right
                0, y0, d
            );
            k = PlaneMesh.write2(uv, k,
                0, v0, // lower left
                1, v0, // lower right
                1, v1, // upper right
                0, v1); // upper left
            n = PlaneMesh.write3(normal, n,
                0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0);

            // Upper horizontal quad
            j = PlaneMesh.write3(pos, j,
                w, y1, 0, // back, upper right
                0, y1, 0, // back, upper left
                0, y1, d, // front, upper left
                w, y1, d
            );
            k = PlaneMesh.write2(uv, k,
                1, v0, // lower left
                0, v0, // lower right
                0, v1, // upper right
                1, v1); // upper left
            n = PlaneMesh.write3(normal, n,
                0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);


            // write the vertical quads
            for (let m = 1; m < cx; m++) {
                const
                    x0 = m * hinc + t * (m - 1), x1 = x0 + t, v0 = i * vt, v1 = v0 + vt;
                j = PlaneMesh.write3(pos, j,
                    x0, y1, d, // front lower left
                    x0, y2, d, // front upper left
                    x0, y2, 0, // back upper left
                    x0, y1, 0
                );
                k = PlaneMesh.write2(uv, k,
                    0, v0, // lower left
                    0, v1, // upper left
                    1, v1,
                    1, v0
                );
                n = PlaneMesh.write3(normal, n,
                    -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0);

                j = PlaneMesh.write3(pos, j,
                    x1, y1, 0, // back lower right
                    x1, y2, 0, // back upper right
                    x1, y2, d, // front upper right
                    x1, y1, d
                );
                k = PlaneMesh.write2(uv, k,
                    0, v0, // lower right
                    0, v1, // upper right
                    1, v1,
                    1, v0
                );
                n = PlaneMesh.write3(normal, n,
                    1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0);
            }
        }
        // write the bottom row of vertical quads
        const y1 = 0, y2 = vinc;
        for (let m = 1; m < cx; m++) {
            const
                x0 = m * hinc + t * (m - 1), x1 = x0 + t, v0 = vt, v1 = v0 + vt;
            j = PlaneMesh.write3(pos, j,
                x0, y1, d, // front lower left
                x0, y2, d, // front upper left
                x0, y2, 0, // back upper left
                x0, y1, 0
            );
            k = PlaneMesh.write2(uv, k,
                0, v0, // lower left
                0, v1, // upper left
                1, v1,
                1, v0
            );
            n = PlaneMesh.write3(normal, n,
                -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0);

            j = PlaneMesh.write3(pos, j,
                x1, y1, 0, // back lower right
                x1, y2, 0, // back upper right
                x1, y2, d, // front upper right
                x1, y1, d
            );
            k = PlaneMesh.write2(uv, k,
                0, v0, // lower right
                0, v1, // upper right
                1, v1,
                1, v0
            );
            n = PlaneMesh.write3(normal, n,
                1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0);
        }

    }
}

/**
 * This is the front edge banding of a diagonal cubby.
 */

export class DiagridFaceMesh extends MeshWriter {
    width: number;
    height: number;
    thickness: number;

    constructor(width: number, height: number, thickness: number) {
        super();
        this.width = width;
        this.height = height;
        this.thickness = thickness;
    }

    get attrs() {
        const result = new Set<Attr>();
        // Count is how many vertexes we have... since we're working with
        // triangles, this is always a multiple of 3.
        const count = 48; // 3 verts times 8 quads, times 2 tris per quad

        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            w = this.width, h = this.height, t = this.thickness, vp = DiagridFaceMesh.diagridPos(w, h, t), vu = DiagridFaceMesh.diagridUV(w, h, t), [_p, pos] = attrMap.byName('position'), [_u, uv] = attrMap.byName('uv');

        let i = 0;
        // Quad 1, 10, 8, 2
        i = PlaneMesh.write3(pos, i,
            vp[1][0], vp[1][1], 0,
            vp[10][0], vp[10][1], 0,
            vp[8][0], vp[8][1], 0,
            vp[2][0], vp[2][1], 0);
        // Quad 1, 5, 12, 10
        i = PlaneMesh.write3(pos, i,
            vp[1][0], vp[1][1], 0,
            vp[5][0], vp[5][1], 0,
            vp[12][0], vp[12][1], 0,
            vp[10][0], vp[10][1], 0);
        // Quad 4, 3, 7, 8
        i = PlaneMesh.write3(pos, i,
            vp[4][0], vp[4][1], 0,
            vp[3][0], vp[3][1], 0,
            vp[7][0], vp[7][1], 0,
            vp[8][0], vp[8][1], 0);
        // Quad 4, 8, 11, 6
        i = PlaneMesh.write3(pos, i,
            vp[4][0], vp[4][1], 0,
            vp[8][0], vp[8][1], 0,
            vp[11][0], vp[11][1], 0,
            vp[6][0], vp[6][1], 0);
        // Quad 16, 12, 9, 14
        i = PlaneMesh.write3(pos, i,
            vp[16][0], vp[16][1], 0,
            vp[12][0], vp[12][1], 0,
            vp[9][0], vp[9][1], 0,
            vp[14][0], vp[14][1], 0);
        // Quad 16, 17, 13, 12
        i = PlaneMesh.write3(pos, i,
            vp[16][0], vp[16][1], 0,
            vp[17][0], vp[17][1], 0,
            vp[13][0], vp[13][1], 0,
            vp[12][0], vp[12][1], 0);
        // Quad 19, 10, 12, 18
        i = PlaneMesh.write3(pos, i,
            vp[19][0], vp[19][1], 0,
            vp[10][0], vp[10][1], 0,
            vp[12][0], vp[12][1], 0,
            vp[18][0], vp[18][1], 0);
        // Quad 19, 15, 8, 10
        i = PlaneMesh.write3(pos, i,
            vp[19][0], vp[19][1], 0,
            vp[15][0], vp[15][1], 0,
            vp[8][0], vp[8][1], 0,
            vp[10][0], vp[10][1], 0);

        // Write the UVs. Elsewhere this u is labeled t, but we'll use u here to
        // avoid conflicting with the alias for the thickness parameter. This u
        // is the scaled version of t for the UV coordinates.
        const
            g = Math.sqrt(w * w + h * h), u = t * (1 / g), u2 = u / 2;

        i = 0;
        // Quad 1: 1, 10, 8, 2
        i = PlaneMesh.write2(uv, i,
            vu[1][0], vu[1][1],
            vu[10][0], vu[10][1],
            0, vu[8][1],
            vu[2][0], vu[2][1]);
        // Quad 2: 1, 5, 12, 10
        i = PlaneMesh.write2(uv, i,
            vu[1][0], vu[1][1],
            vu[5][0], vu[5][1],
            u, vu[12][1],
            vu[10][0], vu[10][1]);
        // Quad 3: 4, 3, 7, 8
        i = PlaneMesh.write2(uv, i,
            vu[4][0], vu[4][1],
            vu[3][0], vu[3][1],
            vu[7][0], vu[7][1],
            u2, vu[8][1]);
        // Quad 4: 4, 8, 11, 6
        i = PlaneMesh.write2(uv, i,
            vu[4][0], vu[4][1],
            u2, vu[8][1],
            vu[11][0], vu[11][1],
            vu[6][0], vu[6][1]);
        // Quad 5: 16, 12, 9, 14
        i = PlaneMesh.write2(uv, i,
            vu[16][0], vu[16][1],
            u2, vu[12][1],
            vu[9][0], vu[9][1],
            vu[14][0], vu[14][1]);
        // Quad 6: 16, 17, 13, 12
        i = PlaneMesh.write2(uv, i,
            vu[16][0], vu[16][1],
            vu[17][0], vu[17][1],
            vu[13][0], vu[13][1],
            u2, vu[12][1]);
        // Quad 7: 19, 10, 12, 18
        i = PlaneMesh.write2(uv, i,
            vu[19][0], vu[19][1],
            vu[10][0], vu[10][1],
            u, vu[12][1],
            vu[18][0], vu[18][1]);
        // Quad 8: 19, 15, 8, 10
        i = PlaneMesh.write2(uv, i,
            vu[19][0], vu[19][1],
            vu[15][0], vu[15][1],
            0, vu[8][1],
            vu[10][0], vu[10][1]);

    }

    /**
     * Generate the X, Y positions of the diagrid face vertexes.
     */
    static diagridPos(w: number, h: number, t: number) {
        const
            a = Math.atan2(w, h), // angle alpha
            x = t / (2 * Math.sin(Math.PI / 2 - a)), y = t / (2 * Math.sin(a)), w2 = w / 2, h2 = h / 2, t2 = t / 2, x2 = x / 2, y2 = y / 2, hmy = h - y, wmx = w - x;
        return [
            [], // Include an extra so we can use 1-based indexes
            [0, h],
            [x, h],
            [wmx, h],
            [w, h],
            [0, hmy],
            [w, hmy],
            [w2, h2 + y],
            [w2 + x2, h2 + y2],
            [w2 - x, h2],
            [w2, h2],
            [w2 + x, h2],
            [w2 - x2, h2 - y2],
            [w2, h2 - y],
            [0, y],
            [w, y],
            [0, 0],
            [x, 0],
            [wmx, 0],
            [w, 0]
        ];
    }

    static diagridUV(w: number, h: number, tw: number) {
        const
            g = Math.sqrt(w * w + h * h), t = tw * (1 / g), t2 = t / 2;
        return [
            [], // Include an extra so we can use 1-based indexes
            [t2, 0],
            [0, 0],
            [0, 1],
            [t2, 1],
            [t, 0],
            [t, 1],
            [0, 0.5],
            [null, 0.5],
            [0, 0.5],
            [t2, 0.5],
            [t, 0.5],
            [null, 0.5],
            [t, 0.5],
            [0, 0],
            [0, 1],
            [t2, 0],
            [t, 0],
            [t, 1],
            [t2, 1]
        ];
    }
}

/**
 * These are the backing shelves of the diagonal cubby.
 */

export class DiagridBoxMesh extends MeshWriter {
    width: number;
    height: number;
    depth: number;
    thickness: number;

    constructor(width: number, height: number, depth: number, thickness: number) {
        super();
        this.width = width;
        this.height = height;
        this.depth = depth;
        this.thickness = thickness;
    }

    get attrs() {
        const result = new Set<Attr>();
        // Count is how many vertexes we have... since we're working with
        // triangles, this is always a multiple of 3.
        const count = 48; // 3 verts times 8 quads, times 2 tris per quad

        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('normal', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            w = this.width, h = this.height, d = this.depth, t = this.thickness, a = Math.atan2(w, h), // angle alpha
            cosa = Math.cos(a), sina = Math.sin(a), vp = DiagridFaceMesh.diagridPos(w, h, t), [_p, pos] = attrMap.byName('position'), [_n, n] = attrMap.byName('normal'), [_u, uv] = attrMap.byName('uv');

        const edges = [
            [7, 2, cosa, sina],
            [3, 7, -cosa, sina],
            [5, 9, -cosa, -sina],
            [9, 14, -cosa, sina],
            [17, 13, cosa, -sina],
            [13, 18, -cosa, -sina],
            [15, 11, cosa, sina],
            [11, 6, cosa, -sina]
        ];

        let i = 0, j = 0;
        for (let [a, b, nx, ny] of edges) {
            PlaneMesh.write3(pos, i,
                vp[a][0], vp[a][1], 0,
                vp[b][0], vp[b][1], 0,
                vp[b][0], vp[b][1], d,
                vp[a][0], vp[a][1], d);
            i = PlaneMesh.write3(n, i,
                nx, ny, 0,
                nx, ny, 0,
                nx, ny, 0,
                nx, ny, 0);
            j = PlaneMesh.write2(uv, j, 0, 0, 1, 0, 1, 1, 0, 1);
        }
    }
}

